import { BrowserFileUtils } from '../utils/BrowserFileUtils'

const UPLOAD_SIGN_KEY = 'cgapi_lqkj2013'

export type UploadProtocolParams = Record<string, string | undefined>

export type UploadProtocolResponse = {
  ret?: number
  code?: number
  msg?: string
}

type UploadInitPayload = {
  ret?: number
  msg?: string
  data?: { tranId?: string; strategy?: { upload_url?: string } }
}

type QueueInfoPayload = {
  ret?: number
  msg?: string
  data?: { queue_count?: number }
}

export type UploadInitResult = {
  tranId: string
  uploadUrl: string
}

export type BuildInitUploadParamsOptions = {
  guid: string
  version: string
  projectVersion: string
  sessionId: string
}

export type BuildUploadTaskParamsOptions = {
  contentMd5: string
  fileName: string
  fileSize: number
  guid: string
  op?: string
  shared?: string
  projectVersion: string
  sessionId: string
  tranId: string
  version: string
}

export type BuildQueueInfoParamsOptions = {
  tranId: string
  sessionId: string
  guid: string
  version: string
  count: number
  timestamp: number
}

/**
 * 生成旧上传协议签名，按 key=value 字符串排序后拼接固定 key。
 * @param params 参与签名的参数
 */
export function buildUploadSign(params: UploadProtocolParams): string {
  const sortedParams = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .sort()
  return BrowserFileUtils.getMd5HashFromString(sortedParams.join('&') + UPLOAD_SIGN_KEY)
}

/**
 * 生成带 sign 的 URL 查询串，保持上传协议字段与签名字段一致。
 * @param params 参与请求和签名的参数
 */
export function buildSignedUploadQuery(params: UploadProtocolParams): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      query.set(key, value)
    }
  }
  query.set('sign', buildUploadSign(params))
  return query.toString()
}

/**
 * 构建 initUpload 参数；该接口使用 project_ver 字段。
 * @param options 上传初始化参数
 */
export function buildInitUploadParams(options: BuildInitUploadParamsOptions): UploadProtocolParams {
  return {
    version: options.version,
    project_ver: options.projectVersion,
    sessionid: options.sessionId,
    guid: options.guid,
  }
}

/**
 * 构建 preCheck 和 upload_url 共享参数；文件名按协议转 UTF-8 十六进制。
 * @param options 单文件上传参数
 */
export function buildUploadTaskParams(options: BuildUploadTaskParamsOptions): UploadProtocolParams {
  return {
    content_md5: options.contentMd5,
    filename: BrowserFileUtils.stringtoHex(options.fileName),
    filesize: String(options.fileSize),
    guid: options.guid,
    op: options.op,
    shared: options.shared,
    proj_ver: options.projectVersion,
    sessionid: options.sessionId,
    tran_id: options.tranId,
    version: options.version,
  }
}

/**
 * 构建 getQueueInfo 参数。
 * @param options 队列查询参数
 */
export function buildQueueInfoParams(options: BuildQueueInfoParamsOptions): UploadProtocolParams {
  return {
    tran_id: options.tranId,
    sessionid: options.sessionId,
    guid: options.guid,
    version: options.version,
    count: String(options.count),
    timestamp: String(options.timestamp),
  }
}

/**
 * 解析上传协议返回体，失败时保留原始响应便于定位服务端问题。
 * @param fileName 上传文件名
 * @param response 接口原始响应文本
 * @param action 当前上传动作
 */
export function parseUploadProtocolResponse(
  fileName: string,
  response: string,
  action: string,
): UploadProtocolResponse {
  try {
    return JSON.parse(response) as UploadProtocolResponse
  } catch {
    throw new Error(`${fileName} ${action}响应解析失败：${response}`)
  }
}

/**
 * 断言上传协议通用成功状态；ret=1 表示接口成功，code 用于业务状态。
 * @param fileName 上传文件名
 * @param payload 已解析的协议响应
 * @param action 当前上传动作
 * @param rawResponse 原始响应文本
 */
export function assertUploadRetSuccess(
  fileName: string,
  payload: UploadProtocolResponse,
  action: string,
  rawResponse?: string,
): void {
  if (payload.ret !== 1) {
    throw new Error(`${fileName} ${action}失败：${payload.msg ?? rawResponse ?? JSON.stringify(payload)}`)
  }
}

/**
 * 判断 preCheck 是否命中秒传；code=2 表示服务器已有文件。
 * @param payload preCheck 响应
 */
export function isPreCheckFastHit(payload: UploadProtocolResponse): boolean {
  return payload.ret === 1 && payload.code === 2
}

/**
 * 断言 upload_url 提交成功；upload_url 的 code 不是失败依据。
 * @param fileName 上传文件名
 * @param response 接口原始响应文本
 */
export function assertUploadSubmitSuccess(fileName: string, response: string): void {
  const payload = parseUploadProtocolResponse(fileName, response, '上传')
  assertUploadRetSuccess(fileName, payload, '上传', response)
}

/**
 * 解析 initUpload 返回体并校验必要字段。
 * @param response 接口原始响应文本
 */
export function parseUploadInitResponse(response: string): UploadInitResult {
  let payload: UploadInitPayload
  try {
    payload = JSON.parse(response) as UploadInitPayload
  } catch {
    throw new Error(`initUpload 响应解析失败：${response}`)
  }
  if (payload.ret !== 1 || !payload.data?.tranId || !payload.data.strategy?.upload_url) {
    throw new Error(`initUpload 返回异常：${payload.msg ?? response}`)
  }
  return {
    tranId: payload.data.tranId,
    uploadUrl: payload.data.strategy.upload_url,
  }
}

/**
 * 解析 getQueueInfo 返回体并校验 ret。
 * @param response 接口原始响应文本
 */
export function parseQueueInfoResponse(response: string): { queueCount: number } {
  let payload: QueueInfoPayload
  try {
    payload = JSON.parse(response) as QueueInfoPayload
  } catch {
    throw new Error(`getQueueInfo 响应解析失败：${response}`)
  }
  if (payload.ret !== 1) {
    throw new Error(`getQueueInfo 返回异常：${payload.msg ?? response}`)
  }
  return {
    queueCount: Number(payload.data?.queue_count ?? 0),
  }
}
